import React, {useEffect, useState} from 'react'
import {Document, Font, Image, Page, PDFViewer, StyleSheet, Text, View} from '@react-pdf/renderer'
import {useParams} from 'react-router-dom'
import {IIncomeDocument} from './IncomeDocumentDetailPage'
import PrintProductSection from './income-document-pdf/PrintProductSection'
import api from '../../utils/Api'
import {IncomeDocumentTypes} from '../../constants/Common'
import LDCLogo from '../../../_metronic/assets/ldc-logo.png'
import moment from 'moment'
import {calculateSummaryPriceInfo, IProductAccountSummary} from '../../utils/ProductHelper'
import {getIncomeDocumentPrintVolume, getTitleLabel} from '../../utils/Helper'
import PrintSignatureSection from './income-document-pdf/PrintSignatureSection'
import PurchaseOrderTable from './income-document-pdf/PurchaseOrderTable'

Font.register({
  family: 'DB-Helvethaica-X',
  fonts: [
    {
      src: require('../../../DB-Helvethaica-X.ttf'),
      fontWeight: 'normal',
    },
    {
      src: require('../../../DB-Helvethaica-X-bold.ttf'),
      fontWeight: 'bold',
    },
  ],
})

Font.register({
  family: 'FontAwesome',
  src: require('../../../fa-solid-900.ttf'),
})

const styles = StyleSheet.create({
  page: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: 12,
    padding: '18px',
    fontFamily: 'DB-Helvethaica-X',
  },
  section: { marginBottom: 12 },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  header: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#3F51B5',
    textAlign: 'right',
    marginBottom: 10
  },
  label: {
    fontWeight: 'bold'
  },
  table: {
    marginTop: 10
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 6,
  },
  tableHeader: {
    backgroundColor: '#E0E7FF',
    fontWeight: 'bold',
    borderRadius: '4px',
  },
  tableCell: {
    flex: 1,
    paddingHorizontal: 4
  },
  summaryBox: {
    backgroundColor: '#F5F5F5',
    padding: 6,
    marginTop: 'auto',
  },
  rightBox: {
    textAlign: 'right'
  },
  signatureBox: {
    borderTopWidth: 1,
    borderColor: '#000',
    marginTop: 20,
    paddingTop: 6,
    textAlign: 'center'
  },
});

const IncomeDocumentPDFA5Page = () => {
  const { id } = useParams()
  const [document, setDocument] = useState<IIncomeDocument | undefined>(undefined)

  useEffect(() => {
    api.request(`account/income-document/${id}`, (res) => {
      setDocument(res)
    })
  }, [])

  if (!document) return <></>

  return (
    <div className={'mx-auto'} style={{height: 622, width: 800}}>
      <PDFViewer showToolbar width='100%' height='100%'>
        <IncomeDocumentPDF document={document} />
      </PDFViewer>
    </div>
  )
}

const IncomeDocumentPDF: React.FC<{document: IIncomeDocument}> = ({ document }) => {
  const [productAccountSummary, setProductAccountSummary] = useState<IProductAccountSummary>({} as  IProductAccountSummary)
  const hasIssueTaxInvoice = document.issueTaxInvoice
  const shouldRenderPayment = [IncomeDocumentTypes.DEPOSIT, IncomeDocumentTypes.RECEIPT, IncomeDocumentTypes.EXPENSE_PURCHASE_INVENTORY_RECORD, IncomeDocumentTypes.EXPENSE_EXPENSE_RECORD].includes(document.type)
  const vatType = document.vatType
  const excludeField: string[] = []
  const isExpensePurchaseOrder = document.type === IncomeDocumentTypes.EXPENSE_PURCHASE_ORDER
  const printVolume = getIncomeDocumentPrintVolume(document)
  const totalPageCount = printVolume.copyPageTotal + printVolume.originalPageTotal;
  const totalPageList = Array.from({ length: totalPageCount }, (_, i) => i + 1);

  useEffect(() => {
    const productPriceSummary =  calculateSummaryPriceInfo(document)
    setProductAccountSummary(productPriceSummary)
  }, [document])

  const productPriceInfo = productAccountSummary.productPriceInfo

  if (!productPriceInfo) return (
    <Document title={`LDC-${document.code}`}>
      <Page size='A5' orientation="landscape" style={styles.page}>
      </Page>
    </Document>
  )

  const getTrailingTitleText = () => {
    switch (document.type) {
      case IncomeDocumentTypes.EXPENSE_REQUEST_PURCHASE_ORDER:
        return '(Purchase Requisition)'
      case IncomeDocumentTypes.EXPENSE_PURCHASE_ORDER:
        return '(Purchasing Order)'
      default:
        return ''
    }
  }
  const titleLabel = getTitleLabel(document, getTrailingTitleText())

  const _renderIconLabel = (icon: string, label: string) => {
    return (
      <View style={{display: 'flex', alignItems: 'center', flexDirection: 'row', gap: '8px',}}>
        <Text style={{fontFamily: 'FontAwesome', fontSize: 9}}>{icon}</Text>
        <Text style={{}}>{label}</Text>
      </View>
    )
  }

  const _rowLabel = (label: string, value: string | number) => {
    return (
      <View style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
        <Text style={{fontWeight: 'bold'}}>{`${label} :`}</Text>
        <Text style={{width: 100, textAlign: 'right'}}>{value}</Text>
      </View>
    )
  }

  const _renderDocInfoRow = (label: string, value: any, labelWidth?: number) => {
    return (
      <View style={{display: 'flex', flexWrap: 'wrap', flexDirection: "row"}}>
        <Text style={{fontWeight: 'bold', width: labelWidth || 51}}>{`${label} :`}</Text>
        <Text style={{flex: 1}}>{value}</Text>
      </View>
    )
  }

  const DocHeadingSection = ({isOriginal}: {isOriginal: boolean}) => {
    return (
      <View style={{display: 'flex', flexDirection: "row", justifyContent: "space-between", alignItems: 'flex-start'}}>
        <Image src={LDCLogo} style={{width: 120, height: 'auto'}} />
        <View style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
          <Text style={{fontSize: 22, fontWeight: 'bold', color: '#3f51b5', textAlign: 'right'}}>
            {titleLabel}
          </Text>
          <Text style={{fontSize: 14, color: '#4c4c4c', textAlign: 'right', marginBottom: 8, marginTop: -6}}>
            {isOriginal ? '( ต้นฉบับ / original )' : '( สำเนา / copy )'}
          </Text>
          {_rowLabel('เลขที่เอกสาร', document.code)}
        </View>
      </View>
    )
  }

  const DocContactSection = () => {
    const LeftSide = () => {
      const labelWidth = 42
      const customer = document.labCompany

      return (
        <View style={{display: 'flex', flexDirection: 'row', flex: 1, paddingTop: '8px'}}>
          {document.type === IncomeDocumentTypes.EXPENSE_PURCHASE_ORDER && (
            <View style={{display: 'flex', flexDirection: 'column', lineHeight: '12px', marginRight: 14}}>
              <Text style={{fontSize: 14, fontWeight: 'bold', color: '#3f51b5', marginBottom: 4}}>{'บริษัท แอลดีซี เด็นทัล จำกัด (มหาชน)'}</Text>
              <Text>{'395-395/1 อาคารแอลดีซี ชั้น 2'}</Text>
              <Text>{'ม.5 ถ.ศรีนครินทร์ ต.สำโรงเหนือ'}</Text>
              <Text>{'อ.เมืองสมุทรปราการ จ.สมุทรปราการ 10270'}</Text>
              <Text>{'เลขประจำตัวผู้เสียภาษีอากร 0107557000080'}</Text>
            </View>
          )}
          <View style={{display: 'flex', flexDirection: 'column', gap: 4, flex: 1}}>
            {_renderDocInfoRow('รหัส', customer.code || '-', labelWidth)}
            {_renderDocInfoRow('ชื่อ', customer.name, labelWidth)}
          </View>
        </View>
      )
    }

    const RightSide = () => {
      const seller = document.seller

      return (
        <View style={{display: 'flex', flexDirection: 'column', gap: 4, width: "26.5%", position: 'relative', top: 6}}>
          <View style={{display: 'flex', flexWrap: 'wrap', flexDirection: "row"}}>
            <Text style={{fontWeight: 'bold'}}>{`วันที่ :`}</Text>
            <Text style={{flex: 1, textAlign: 'right'}}>{moment(document.date).format('DD/MM/YYYY')}</Text>
          </View>
          <View style={{display: 'flex', flexWrap: 'wrap', flexDirection: "row"}}>
            <Text style={{fontWeight: 'bold'}}>{`กำหนดส่ง :`}</Text>
            <Text style={{flex: 1, textAlign: 'right'}}>{moment(document.expiryDate).format('DD/MM/YYYY')}</Text>
          </View>
        </View>
      )
    }

    return (
      <View style={{display: 'flex', flexDirection: "row", gap: '1rem'}}>
        <LeftSide />
        <RightSide />
      </View>
    )
  }

  return (
    <Document title={`LDC-${document.code}`}>
      {totalPageList.map((_, index) => {
        return (
          <Page key={`doc-${document.code}-${index}`} size='A5' orientation={'landscape'} style={[styles.page, {paddingBottom: 26}]}>
            <DocHeadingSection isOriginal={index === 0} />
            <DocContactSection />
            {isExpensePurchaseOrder
              ? <PurchaseOrderTable document={document} />
              : <PrintProductSection document={document} />
            }
            {!isExpensePurchaseOrder && (
              <PrintSignatureSection
                document={document}
                styles={{marginTop: 'auto'}}
              />
            )}
            {/* เลขหน้าทุกแผ่น ให้จัดซื้อจับชุดเอกสารได้ — นับแยกต้นฉบับ/สำเนา (subPage = แผ่นใน <Page> นี้) (29 ก.ย. 2569) */}
            <Text
              fixed
              style={{position: 'absolute', bottom: 6, right: 18, fontSize: 10, color: '#4c4c4c'}}
              render={({subPageNumber, subPageTotalPages}: any) =>
                `${document.code}  ${index === 0 ? 'ต้นฉบับ' : 'สำเนา'}  หน้า ${subPageNumber}/${subPageTotalPages}`
              }
            />
          </Page>
        )
      })}
    </Document>
  )
}

export default IncomeDocumentPDFA5Page;
